import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
    FileAttachmentApiService,
    OrganizationApiService,
    OrganizationDocumentPageResponse,
    OrganizationResponse,
    ReferenceBookResponse,
    ReferenceBookType,
    UserAttributesApiService,
    UserPageResponse,
} from '@npck/af-frontend-api';
import {downloadFromBlobResponse, NESTED_TAB, RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {EMPTY, mergeMap, Subscription, zip} from 'rxjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../../components/delete-dialog/delete-dialog.component';
import {FilterField} from '../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {Column, ColumnType, CustomLayoutColumn, Row, StatusColumn} from '../../../../components/full-table/table/table.component';
import {StatusStyle} from '../../../../components/status/status.component';
import {BIN_REFERENCES, DictionaryHolderService, findValueInDictionary} from '../../../../domain/dictionary-holder.service';
import {OrganizationDetailsFilterHolderService} from '../../../../domain/organization-details-filter-holder.service';
import {PasswordExpiryStatusService} from '../../../../domain/password-expiry-status.service';
import {ORGANIZATION_DOCUMENT_ID, ORGANIZATION_ID, USER_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AdminPanelTab} from '../../admin.component';
import {ORGANIZATION_DOCUMENT_DELETION_SCOPE, ORGANIZATION_SCOPE} from '../create-edit/organization-create-edit.component';

const SEARCH_FILTER: FilterField[] = [{type: 'text', name: 'query'}];
const ORGANIZATION_DELETED_REASON = 'ORGANIZATION_DELETED';

@Component({
    templateUrl: 'organization-details.component.html',
    styleUrls: ['organization-details.component.scss'],
    standalone: false,
})
export class OrganizationDetailsComponent implements OnInit, OnDestroy {
    Path = Path;
    StatusStyle = StatusStyle;
    organizationId: string;
    organization?: OrganizationResponse;
    activeTab: OrganizationTab;
    selectedTabIndex: number;
    tabs: OrganizationTab[] = ['tabOrganizationUsers', 'tabOrganizationDocuments'];
    tabPathToTab: Record<string, AdminPanelTab> = {[Path.ORGANIZATIONS]: 'tabOrganizations'};
    filterFields = SEARCH_FILTER;

    usersTableEvent: TableEvent;
    usersDataSource: Row[] = [];
    usersTotalElements: number;

    documentsTableEvent: TableEvent;
    documentsDataSource: Row[] = [];
    documentsTotalElements: number;
    documentTypes: ReferenceBookResponse[] = [];
    deletionReasons: ReferenceBookResponse[] = [];
    deletedBaseName: string;

    private usersSubscription: Subscription;
    private documentsSubscription: Subscription;

    constructor(
        private organizationApiService: OrganizationApiService,
        private userAttributesApiService: UserAttributesApiService,
        private fileAttachmentApiService: FileAttachmentApiService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        private dialogService: DialogService,
        private snackbarService: SnackbarService,
        private dictionaryHolderService: DictionaryHolderService,
        private filterHolderService: OrganizationDetailsFilterHolderService,
        private passwordExpiryStatusService: PasswordExpiryStatusService,
    ) {
        this.organizationId = this.activatedRoute.snapshot.paramMap.get(ORGANIZATION_ID);
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_TAB) as OrganizationTab || 'tabOrganizationUsers';
        this.selectedTabIndex = Math.max(0, this.tabs.indexOf(this.activeTab));
    }

    ngOnInit(): void {
        this.usersTableEvent = this.filterHolderService.getState(this.organizationId, 'tabOrganizationUsers')
            ?? {...initialTableEvent};
        this.documentsTableEvent = this.filterHolderService.getState(this.organizationId, 'tabOrganizationDocuments')
            ?? {...initialTableEvent};
        this.loadOrganization();
    }

    ngOnDestroy(): void {
        this.usersSubscription?.unsubscribe();
        this.documentsSubscription?.unsubscribe();
    }

    selectTab(index: number): void {
        if (this.tabs[index] === this.activeTab) {
            return;
        }
        this.activeTab = this.tabs[index];
        this.selectedTabIndex = index;
        this.routerService.navigate(
            `${Path.ADMIN_PANEL_ORGANIZATIONS}/${this.organizationId}`,
            {
                queryParams: {[TAB]: 'tabOrganizations', [NESTED_TAB]: this.activeTab},
                replaceUrl: true,
            });
        this.loadActiveTab();
    }

    editOrganization(): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            Path.ADMIN_PANEL_ORGANIZATION_EDIT.replace(`:${ORGANIZATION_ID}`, this.organizationId), 'tabOrganizations');
    }

    createUser(): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            Path.ADMIN_PANEL_ORGANIZATION_USER_CREATE.replace(`:${ORGANIZATION_ID}`, this.organizationId), 'tabOrganizations');
    }

    addDocument(): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            Path.ADMIN_PANEL_ORGANIZATION_DOCUMENT_CREATE.replace(`:${ORGANIZATION_ID}`, this.organizationId),
            'tabOrganizations',
            {queryParams: {[NESTED_TAB]: 'tabOrganizationDocuments'}});
    }

    deleteOrganization(): void {
        this.dialogService.openCustomDialogFitContent<DeleteDialogComponent, DeleteDialogResult>(
            DeleteDialogComponent,
            <DeleteDialogData>{
                id: this.organizationId,
                title: 'i18n_af_organizationDeleteDialogTitle',
                subtitle: 'i18n_af_organizationDeleteDialogSubtitle',
                showReason: true,
                reasonFilter: [ORGANIZATION_SCOPE],
            })
            .pipe(mergeMap(result => !result?.reason && EMPTY || this.organizationApiService.deleteOrganization(this.organizationId, {
                deletionReason: result.reason,
                comment: result.comment,
            }).pipe(rxSpinner('deleting'))))
            .subscribe(organization => {
                this.setOrganization(organization);
                this.dictionaryHolderService.clearReferences(BIN_REFERENCES);
                this.loadActiveTab();
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageOrganizationDeleted', 'success', false,
                    {organizationName: organization.nameRu});
            });
    }

    restoreOrganization(): void {
        this.dialogService.openDialog({
            title: 'i18n_af_organizationRestoreDialogTitle',
            subtitle: 'i18n_af_organizationRestoreDialogSubtitle',
            buttonOkText: 'i18n_af_buttonConfirm',
            buttonCancelText: 'i18n_af_buttonCancel',
            translationParams: {organizationName: this.organization?.nameRu},
        })
            .pipe(mergeMap(confirmed => !confirmed && EMPTY
                || this.organizationApiService.restoreOrganization(this.organizationId).pipe(rxSpinner('restoring'))))
            .subscribe(organization => {
                this.setOrganization(organization);
                this.dictionaryHolderService.clearReferences(BIN_REFERENCES);
                this.loadActiveTab();
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageOrganizationRestored', 'success', false,
                    {organizationName: organization.nameRu});
            });
    }

    loadUsers(tableEvent: TableEvent): void {
        this.usersTableEvent = this.filterHolderService.storeTableState(
            this.organizationId, 'tabOrganizationUsers', mergeTableEvent(this.usersTableEvent, tableEvent));
        this.usersSubscription?.unsubscribe();
        this.usersSubscription = this.userAttributesApiService.getUsers({
            bin: this.organization?.bin,
            search: this.usersTableEvent.filter?.query,
            limit: this.usersTableEvent.page?.pageSize,
            offset: this.usersTableEvent.page?.pageOffset,
        })
            .pipe(rxSpinner(this.usersTableEvent.spinnerId))
            .subscribe(response => this.buildUsersDataSource(response));
    }

    loadDocuments(tableEvent: TableEvent): void {
        this.documentsTableEvent = this.filterHolderService.storeTableState(
            this.organizationId, 'tabOrganizationDocuments', mergeTableEvent(this.documentsTableEvent, tableEvent));
        this.documentsSubscription?.unsubscribe();
        this.documentsSubscription = zip([
            this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
            this.organizationApiService.getOrganizationDocuments(this.organizationId, {
                search: this.documentsTableEvent.filter?.query,
                limit: this.documentsTableEvent.page?.pageSize,
                offset: this.documentsTableEvent.page?.pageOffset,
                sortBy: this.documentsTableEvent.sortBy?.columnName,
                sortOrder: this.documentsTableEvent.sortBy?.direction,
            }),
        ])
            .pipe(rxSpinner(this.documentsTableEvent.spinnerId))
            .subscribe(([documentTypes, response]) => {
                this.documentTypes = documentTypes;
                this.buildDocumentsDataSource(response);
            });
    }

    private loadOrganization(): void {
        Spinner.show('initial');
        zip([
            this.organizationApiService.getOrganization(this.organizationId),
            this.dictionaryHolderService.getReference(ReferenceBookType.DELETED_BASED),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe(([organization, deletionReasons]) => {
                this.deletionReasons = deletionReasons;
                this.setOrganization(organization);
                this.loadActiveTab();
            });
    }

    private setOrganization(organization: OrganizationResponse): void {
        this.organization = organization;
        this.deletedBaseName = organization.deletionReason
            ? findValueInDictionary(this.deletionReasons, organization.deletionReason, 'name_ru')
            : '';
    }

    private loadActiveTab(): void {
        if (this.activeTab === 'tabOrganizationDocuments') {
            this.loadDocuments({spinnerId: 'initial'});
        } else {
            this.loadUsers({spinnerId: 'initial'});
        }
    }

    private buildUsersDataSource(response: UserPageResponse): void {
        this.usersTotalElements = response.total;
        this.usersDataSource = response.items.map(item => <Row>{
            click: () => this.openUserDetails(item.id),
            isEnabled: <StatusColumn>{
                type: ColumnType.STATUS,
                text: !item.isDeleted ? 'i18n_af_statusEnabled' : 'i18n_af_statusDisabled',
                style: !item.isDeleted ? StatusStyle.SUCCESS : StatusStyle.ERROR,
            },
            login: <Column>{type: ColumnType.TEXT, text: item.username},
            userFullName: <Column>{type: ColumnType.TEXT, text: [item.lastName, item.firstName].filter(n => !!n).join(' ')},
            iin: <Column>{type: ColumnType.TEXT, text: item.iin},
            email: <Column>{type: ColumnType.TEXT, text: item.email},
            createdDate: <Column>{type: ColumnType.DATE, text: item.createdDate},
            createdByUser: <Column>{type: ColumnType.TEXT, text: item.createdBy},
            lastLoginDateTime: <Column>{type: ColumnType.DATETIME, text: item.lastLoginDateTime},
            passwordExpiry: <StatusColumn>{
                type: ColumnType.STATUS,
                ...this.passwordExpiryStatusService.status(item.passwordExpiryDaysLeft),
            },
            deletedDate: <Column>{type: ColumnType.DATE, text: item.deletedDate},
            deletedByUser: <Column>{type: ColumnType.TEXT, text: item.deletedBy},
            deactivationReason: <Column>{
                type: ColumnType.TEXT,
                text: item.deactivationReason ? `i18n_af_deactivationReason#${item.deactivationReason}` : '',
            },
        });
    }

    private buildDocumentsDataSource(response: OrganizationDocumentPageResponse): void {
        this.documentsTotalElements = response.total;
        this.documentsDataSource = response.items.map(item => <Row>{
            actions: <CustomLayoutColumn>{
                type: ColumnType.CUSTOM_LAYOUT,
                cellStyle: {
                    layoutDirection: 'row',
                    layoutAlignFirst: 'start',
                    layoutAlignSecond: 'center',
                    layoutGap: '8px',
                },
                layoutContentItems: [
                    ...(item.isDeleted ? [] : [
                        {
                            type: 'icon',
                            text: 'ic_edit',
                            click: () => this.editDocument(item.id),
                            tooltip: 'i18n_af_labelEdit',
                            iconSize: 'i-16',
                        },
                        {
                            type: 'icon',
                            text: 'ic_delete',
                            click: () => this.deleteDocument(item.id, findValueInDictionary(this.documentTypes, item.type, 'name_ru')),
                            tooltip: 'i18n_af_labelDelete',
                            iconSize: 'i-16',
                        },
                    ]),
                    {
                        type: 'icon',
                        text: 'ic_download',
                        click: () => this.downloadDocument(item.fileId),
                        tooltip: 'i18n_af_labelDownload',
                        iconSize: 'i-16',
                    },
                ],
            },
            documentStatus: <StatusColumn>{
                type: ColumnType.STATUS,
                text: !item.isDeleted ? 'i18n_af_statusEnabled' : 'i18n_af_statusDeleted',
                style: !item.isDeleted ? StatusStyle.SUCCESS : StatusStyle.ERROR,
            },
            documentType: <Column>{
                type: ColumnType.TEXT,
                text: findValueInDictionary(this.documentTypes, item.type, 'name_ru') || item.type,
            },
            fileName: <Column>{type: ColumnType.TEXT, text: item.fileName},
            createdByUser: <Column>{type: ColumnType.TEXT, text: item.createdBy},
            createdDate: <Column>{type: ColumnType.DATE, text: item.createdDate},
            deletedDate: <Column>{type: ColumnType.DATE, text: item.deletedDate},
            deletedByUser: <Column>{type: ColumnType.TEXT, text: item.deletedBy},
            deletionReason: <Column>{
                type: ColumnType.TEXT,
                text: this.documentDeletionReason(item.deletionReason),
            },
        });
    }

    private downloadDocument(fileId: string): void {
        this.fileAttachmentApiService.downloadFile(fileId, 'response')
            .pipe(rxSpinner(`downloadFile_${fileId}`))
            .subscribe(response => downloadFromBlobResponse(response));
    }

    private documentDeletionReason(reason?: string): string {
        if (!reason) {
            return '';
        }
        return reason === ORGANIZATION_DELETED_REASON
            ? `i18n_af_deactivationReason#${ORGANIZATION_DELETED_REASON}`
            : findValueInDictionary(this.deletionReasons, reason, 'name_ru');
    }

    private openUserDetails(userId: string): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            Path.ADMIN_PANEL_ORGANIZATION_USER_DETAILS
                .replace(`:${ORGANIZATION_ID}`, this.organizationId)
                .replace(`:${USER_ID}`, userId),
            'tabOrganizations');
    }

    private editDocument(documentId: string): void {
        this.routerService.navigateToTab<AdminPanelTab>(
            Path.ADMIN_PANEL_ORGANIZATION_DOCUMENT_EDIT
                .replace(`:${ORGANIZATION_ID}`, this.organizationId)
                .replace(`:${ORGANIZATION_DOCUMENT_ID}`, documentId),
            'tabOrganizations',
            {queryParams: {[NESTED_TAB]: 'tabOrganizationDocuments'}});
    }

    private deleteDocument(documentId: string, documentType: string): void {
        this.dialogService.openCustomDialogFitContent<DeleteDialogComponent, DeleteDialogResult>(
            DeleteDialogComponent,
            <DeleteDialogData>{
                id: documentId,
                title: 'i18n_af_organizationDocumentDeleteDialogTitle',
                subtitle: 'i18n_af_organizationDocumentDeleteDialogSubtitle',
                showReason: true,
                reasonFilter: [ORGANIZATION_DOCUMENT_DELETION_SCOPE],
            })
            .pipe(mergeMap(result => !result?.reason && EMPTY
                || this.organizationApiService.deleteOrganizationDocument(this.organizationId, documentId, {
                    deletionReason: result.reason,
                }).pipe(rxSpinner('deletingDocument'))))
            .subscribe(() => {
                this.snackbarService.showSnackBar('i18n_af_snackbarMessageOrganizationDocumentDeleted', 'success', false,
                    {documentType: documentType});
                this.loadDocuments({spinnerId: 'initial'});
            });
    }
}

export declare type OrganizationTab = 'tabOrganizationUsers' | 'tabOrganizationDocuments';

export interface DeleteDialogResult {
    comment: string;
    reason: string | null;
}
